import FormulaParserPkg from 'fast-formula-parser'
import type { Cell, CellValue, Sheet } from './types'
import { ref } from './a1'
import { CUSTOM } from './functions'

// CJS 패키지 — default 로 FormulaParser 클래스가 오고 정적 프로퍼티로 부속물이 붙어 있다.
const FormulaParser: any = (FormulaParserPkg as any)?.default ?? FormulaParserPkg
const SSF: any = FormulaParser.SSF

export interface Evaluated {
  /** 계산된 원시 값 (숫자/문자/불리언/에러문자열) */
  value: CellValue
  /** 화면 표시 문자열 (numFmt 적용) */
  text: string
  error?: boolean
}

const EMPTY: Evaluated = { value: null, text: '' }

function isFormulaError(v: any): boolean {
  return !!v && typeof v === 'object' && typeof v.toString === 'function' && /^#[A-Z0-9/!?]+$/.test(String(v))
}

/**
 * 워크북 스냅샷 하나에 대한 평가기.
 * 셀을 요구할 때마다 재귀적으로 계산하고 캐시한다 (순환참조는 #CIRCULAR!).
 * 워크북이 바뀌면 새로 만든다 — 캐시 무효화 로직을 따로 두지 않기 위한 의도적 설계.
 */
export function createEvaluator(sheets: Sheet[], defaultSheet: string) {
  const byName = new Map<string, Sheet>()
  for (const s of sheets) byName.set(s.name.toLowerCase(), s)

  const cache = new Map<string, CellValue>()
  const visiting = new Set<string>()
  /** #ERROR! 가 난 셀의 실제 예외 메시지 (진단용) */
  const lastErrors = new Map<string, string>()

  const parser = new FormulaParser({
    functions: CUSTOM.functions,
    onCell: ({ sheet, row, col }: any) => raw(sheet ?? defaultSheet, row, col),
    onRange: ({ sheet, from, to }: any) => {
      const out: CellValue[][] = []
      const s = byName.get(String(sheet ?? defaultSheet).toLowerCase())
      const maxRow = Math.min(to.row, Math.max(s?.rows ?? 0, 1))
      const maxCol = Math.min(to.col, Math.max(s?.cols ?? 0, 1))
      for (let r = from.row; r <= maxRow; r++) {
        const line: CellValue[] = []
        for (let c = from.col; c <= maxCol; c++) line.push(raw(sheet ?? defaultSheet, r, c))
        out.push(line)
      }
      return out.length ? out : [[null]]
    },
  })

  function raw(sheetName: string, row: number, col: number): CellValue {
    const sheet = byName.get(String(sheetName).toLowerCase())
    if (!sheet) return null
    const key = `${sheet.name}!${ref(row, col)}`
    if (cache.has(key)) return cache.get(key)!

    const cell: Cell | undefined = sheet.cells[ref(row, col)]
    if (!cell) return null

    if (cell.f) {
      if (visiting.has(key)) return '#CIRCULAR!'
      visiting.add(key)
      let out: CellValue
      try {
        const r = parser.parse(cell.f.replace(/^=/, ''), { sheet: sheet.name, row, col })
        out = isFormulaError(r) ? String(r) : (r as CellValue)
      } catch (e) {
        out = '#ERROR!'
        lastErrors.set(key, e instanceof Error ? `${e.name}: ${e.message}` : String(e))
      } finally {
        visiting.delete(key)
      }
      if (out === undefined) out = null
      cache.set(key, out)
      return out
    }

    const v = cell.v ?? null
    cache.set(key, v)
    return v
  }

  function display(sheetName: string, row: number, col: number): Evaluated {
    const sheet = byName.get(String(sheetName).toLowerCase())
    if (!sheet) return EMPTY
    const cell = sheet.cells[ref(row, col)]
    if (!cell) return EMPTY
    const v = raw(sheet.name, row, col)
    if (v === null || v === '') return { ...EMPTY, value: v }
    const err = typeof v === 'string' && /^#[A-Z0-9/!?]+$/.test(v)
    return { value: v, text: format(v, cell.s?.numFmt), error: err }
  }

  return { raw, display, errorAt: (sheetName: string, row: number, col: number) => lastErrors.get(`${sheetName}!${ref(row, col)}`) }
}

export function format(v: CellValue, numFmt?: string): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE'
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return '#NUM!'
    if (numFmt && numFmt !== 'General') {
      try {
        return String(SSF.format(numFmt, v))
      } catch {
        /* 형식 문자열이 잘못된 경우 원시값으로 폴백 */
      }
    }
    // 부동소수 잡티 제거 — Excel 과 같이 유효숫자 15자리로 절사
    return String(Number(v.toPrecision(15)))
  }
  return String(v)
}

/** 사용자가 셀에 입력한 문자열을 Cell 로 해석한다. */
export function parseInput(input: string): Pick<Cell, 'v' | 'f'> {
  const s = input
  if (s === '') return { v: null }
  if (s.startsWith('=')) return { f: s }
  const t = s.trim()
  if (/^(TRUE|FALSE)$/i.test(t)) return { v: /^true$/i.test(t) }
  // 1,234.5 / 12% / -3.2e4 등을 숫자로
  if (/^-?[\d,]*\.?\d+(e[-+]?\d+)?%?$/i.test(t) && /\d/.test(t)) {
    const pct = t.endsWith('%')
    const n = Number((pct ? t.slice(0, -1) : t).replace(/,/g, ''))
    if (Number.isFinite(n)) return { v: pct ? n / 100 : n }
  }
  return { v: s }
}

/** 내장(정상 구현) + formulajs 로 보강한 것 전체 */
export const SUPPORTED_FUNCTIONS: string[] = (() => {
  try {
    const base: string[] = new FormulaParser().supportedFunctions()
    return Array.from(new Set([...base, ...CUSTOM.filledStubs, ...CUSTOM.added])).sort()
  } catch {
    return []
  }
})()
